/**
 * Document Types for MEV-Med Frontend
 * Espelho dos contratos do motor de emissão de documentos (backend `src/types/document.ts`,
 * `src/services/DocumentEmissionService.ts` e `src/services/SignatoryResolver.ts`).
 *
 * @version 2026.1.0
 * @description Tipos da emissão de documentos (PGR e demais laudos configurados)
 */

import type { ReportType } from './occupationalRisk';
import type { RiskApplicationScope } from './riskApplication';

export type { ReportType, RiskApplicationScope };

export type CompanyModule = 'medicine' | 'safety' | 'environment' | 'esocial';

export type SignatoryType =
  | 'TECHNICAL_RESPONSIBLE'
  | 'MEDICAL_RESPONSIBLE'
  | 'ENVIRONMENTAL_RESPONSIBLE'
  | 'LEGAL_RESPONSIBLE';

/** Item do bloco de assinaturas: ou um responsável da empresa, ou uma declaração. */
export type SignatureBlockItem =
  /** `artId` omitido = escolha automática do backend: a ART válida mais recente. */
  | { kind: 'signatory'; companyResponsibleId: number; artId?: number }
  | { kind: 'declaration'; standardTextId: number };

export type PendencySeverity = 'bloqueia' | 'avisa';

/**
 * Espelha `PendencyAction` do backend: o caminho de saída da pendência.
 *
 * Quem monta a rota é o backend — só ele sabe DE QUEM é o pré-requisito que
 * falta (a ART é de um profissional específico). A tela decide apenas entre
 * mostrar o link e mostrar o recado, conforme `requiredPermission`: botão que
 * levaria a um 403 é pior que nenhum botão.
 */
export interface PendencyAction {
  route: string;
  label: string;
  /** Permissão exigida pela API do destino, no formato `modulo.acao`. */
  requiredPermission: string;
}

export interface Pendency {
  code: string;
  message: string;
  severity: PendencySeverity;
  sectionKey?: string;
  /** Ausente na pendência que não tem tela onde se resolver. */
  action?: PendencyAction;
}

/** O que a tela precisa saber de um tipo antes de escolhê-lo (`GET /documents/types`). */
export interface DocumentTypeSummary {
  type: ReportType;
  label: string;
  requiredModule: CompanyModule;
  allowedScopes: RiskApplicationScope[];
  suggestedSignatories: SignatoryType[];
}

/**
 * ART como o bloco de assinaturas a enxerga. `expired` é CALCULADO pelo backend
 * contra a data de emissão — não é coluna do banco, e não se recalcula aqui.
 */
export interface SignatoryArt {
  id: number;
  number: string;
  /** 'YYYY-MM-DD' */
  issueDate: string;
  /** 'YYYY-MM-DD' ou null quando a ART não vence. */
  expiryDate: string | null;
  expired: boolean;
}

export interface AvailableSignatory {
  companyResponsibleId: number;
  /**
   * O profissional por trás do responsável — é dele a ART. Serve para o seletor
   * levar ao cadastro quem ficou sem ART válida. Ausente quando o responsável
   * não tem profissional vinculado.
   */
  professionalId?: number;
  name: string;
  responsibleType: string;
  /** O tipo de vínculo, que agrupa e sugere — não é o que sai impresso. */
  roleLabel: string;
  /** O texto que sai embaixo da linha de assinatura: profissão, ou o vínculo como reserva. */
  printedRole: string;
  /** Papel previsto pelo tipo de documento — vem pré-selecionado na tela. */
  suggested: boolean;
  /** Vazios quando o profissional não tem registro cadastrado — não impede assinar. */
  council: string;
  registration: string;
  /**
   * TODAS as ARTs do profissional naquela empresa, vencidas inclusive. Quem
   * escolhe é o usuário: filtrar as vencidas deixaria sem nenhuma opção quem só
   * tem vencida, que é justamente o caso em que ele precisa ver o aviso.
   */
  arts: SignatoryArt[];
}

export interface AvailableDeclaration {
  standardTextId: number;
  title: string;
  category: string;
}

export interface AvailableSignatureItems {
  signatories: AvailableSignatory[];
  declarations: AvailableDeclaration[];
}

export interface EmissionRequest {
  type: ReportType;
  scope: RiskApplicationScope;
  scopeId: number;
  /** 'YYYY-MM-DD' */
  periodStart: string;
  /** 'YYYY-MM-DD' */
  periodEnd: string;
  signatureBlock?: SignatureBlockItem[];
}

export interface PreviewResult {
  html: string;
  pendencies: Pendency[];
}

export type DocumentFormat = 'pdf' | 'docx';

/** Arquivo gerado, já com o nome que o backend escolheu no Content-Disposition. */
export interface GeneratedDocumentFile {
  blob: Blob;
  filename: string;
}

export const PENDENCY_SEVERITY_LABELS: Record<PendencySeverity, string> = {
  bloqueia: 'Impede a emissão',
  avisa: 'Aviso',
};

export const DOCUMENT_FORMAT_LABELS: Record<DocumentFormat, string> = {
  pdf: 'PDF',
  docx: 'Word (DOCX)',
};

// ───────────────────────────── Histórico de documentos emitidos (#344) ─────────────────────

/**
 * As cinco situações de um documento, na ordem em que são avaliadas: a primeira que casar
 * vence. Espelha `EmissionStatus` do backend — a mesma ordem, para a tela e o servidor não
 * classificarem a mesma emissão de dois jeitos.
 */
export type EmissionStatus = 'substituida' | 'vencido' | 'aVencer' | 'vigente' | 'semValidade';

/** As quatro que a listagem conta: "substituída" não entra, porque a lista só traz a vigente. */
export type EmissionSummaryStatus = Exclude<EmissionStatus, 'substituida'>;

/** Um assinante como ele foi congelado na emissão — não é o cadastro de hoje. */
export interface EmissionSignatorySnapshot {
  name: string;
  role: string;
  council: string;
  registration: string;
  artNumber: string | null;
}

export interface EmissionSignatureSnapshot {
  signatories: EmissionSignatorySnapshot[];
  declarations: number;
}

/** Um documento na revisão vigente, como a listagem o mostra. Datas em ISO. */
export interface DocumentEmissionRecord {
  id: number;
  type: ReportType;
  typeLabel: string;
  scope: RiskApplicationScope;
  scopeId: number;
  /** O escopo por NOME — a tela nunca mostra o identificador. */
  scopeName: string;
  periodStart: string;
  periodEnd: string;
  /**
   * O número da revisão vigente. As revisões são contíguas a partir de 1, então ele também
   * é o TOTAL de revisões da chave — é daí que sai o "3 de 3", sem outra chamada.
   */
  revision: number;
  emittedAt: string;
  validUntil: string | null;
  technicalResponsibleName: string | null;
  /** Nulo quando a gravação do arquivo falhou: a revisão fica, o arquivo não. */
  pdfPath: string | null;
  docxPath: string | null;
}

/** Uma revisão no diálogo: o que a listagem traz, mais o que só o histórico mostra. */
export interface DocumentEmissionRevision extends DocumentEmissionRecord {
  isLatest: boolean;
  emittedByName: string | null;
  signatureSnapshot: EmissionSignatureSnapshot | null;
}

export interface EmissionListFilters {
  companyId?: number;
  type?: string;
  /** 'YYYY-MM-DD' */
  periodStart?: string;
  /** 'YYYY-MM-DD' */
  periodEnd?: string;
  status?: EmissionSummaryStatus;
  page?: number;
  limit?: number;
}

/**
 * A contagem por situação, calculada pelo servidor sobre o filtro INTEIRO menos o filtro de
 * situação. Contar sobre a página carregada daria número errado da segunda página em diante.
 */
export type EmissionSummary = Record<EmissionSummaryStatus, number>;

export interface EmissionPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EmissionListResult {
  data: DocumentEmissionRecord[];
  pagination: EmissionPagination;
  summary: EmissionSummary;
}

/** A chave dentro da qual as revisões são contadas — o período faz parte dela. */
export interface EmissionRevisionKey {
  type: string;
  scope: RiskApplicationScope;
  scopeId: number;
  /** 'YYYY-MM-DD' */
  periodStart: string;
  /** 'YYYY-MM-DD' */
  periodEnd: string;
}

export const EMISSION_STATUS_LABELS: Record<EmissionStatus, string> = {
  substituida: 'Substituída',
  vencido: 'Vencido',
  aVencer: 'A vencer',
  vigente: 'Vigente',
  semValidade: 'Sem validade definida',
};
